import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Box, CircularProgress, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeSlash, faEye, faInfoCircle, faLock } from "@fortawesome/free-solid-svg-icons";
import { notification } from "antd";
import { login, verifyLoginOtpThirdParty } from "../../../controllers/accounts/Account";
import "../winningVouchers.css";

const LoginPopup = ({ open, onClose, onLoginSuccess, embedded = false }) => {
  const [loginFormData, setLoginFormData] = useState({
    email: "",
    password: "",
    otp: "",
  });
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});
  const [loginSuccess, setLoginSuccess] = useState("");
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [isCounting, setIsCounting] = useState(false);

  // OTP Countdown effect
  useEffect(() => {
    let countdownInterval;
    if (isCounting) {
      countdownInterval = setInterval(() => {
        if (countdown > 0) {
          setCountdown((prevCountdown) => prevCountdown - 1);
        } else {
          clearInterval(countdownInterval);
          setIsCounting(false);
        }
      }, 1000);
    }
    return () => {
      clearInterval(countdownInterval);
    };
  }, [countdown, isCounting]);

  // Reset form when popup closes
  useEffect(() => {
    if (!open) {
      setLoginFormData({ email: "", password: "", otp: "" });
      setIsOTPSent(false);
      setLoginErrors({});
      setLoginSuccess("");
      setShowPassword(false);
      setCountdown(60);
      setIsCounting(false);
    }
  }, [open]);

  // Handle login form change
  const handleLoginChange = (e) => {
    setLoginFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setLoginErrors((prev) => ({
      ...prev,
      [e.target.name]: "",
      formError: "",
    }));
  };

  // Handle login (send OTP)
  const handleLogin = async () => {
    setIsLoginLoading(true);
    try {
      if (!loginFormData?.email) {
        setLoginErrors({ email: "Email is required." });
        setIsLoginLoading(false);
        return;
      }
      if (!loginFormData.password) {
        setLoginErrors({ password: "Password is required." });
        setIsLoginLoading(false);
        return;
      }

      const options = { email: loginFormData?.email, password: loginFormData.password };
      const result = await login(options);

      if (result.status === true) {
        setIsOTPSent(true);
        setLoginSuccess(result.message);
        setCountdown(60);
        setIsCounting(true);
        setLoginErrors({});
      } else {
        setIsOTPSent(false);
        setLoginErrors({ formError: result.message });
      }
    } catch (error) {
      setLoginErrors({ formError: "Something went wrong. Please try again." });
    } finally {
      setIsLoginLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOTP = async () => {
    setIsLoginLoading(true);
    try {
      if (!loginFormData?.email) {
        setLoginErrors({ email: "Email is required." });
        setIsLoginLoading(false);
        return;
      }
      if (!loginFormData.otp) {
        setLoginErrors({ otp: "One Time Password is required." });
        setIsLoginLoading(false);
        return;
      }

      const options = {
        email: loginFormData?.email,
        otp: loginFormData.otp,
      };
      const result = await verifyLoginOtpThirdParty(options);

      if (result.status === true) {
        notification.open({
          message: "Success",
          description: result.message,
          placement: "topRight",
          icon: <FontAwesomeIcon style={{ color: "green" }} icon={faInfoCircle} />,
          duration: 2,
        });
        // Reset form
        setLoginFormData({ email: "", password: "", otp: "" });
        setIsOTPSent(false);
        setIsCounting(false);
        setCountdown(60);
        // Call success callback
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        setLoginErrors({ formError: result.message });
      }
    } catch (error) {
      setLoginErrors({ formError: "Something went wrong. Please try again." });
    } finally {
      setIsLoginLoading(false);
    }
  };

  // Handle email change (reset OTP)
  const handleEmailChange = () => {
    setLoginFormData((prev) => ({
      ...prev,
      password: "",
      otp: "",
    }));
    setIsOTPSent(false);
    setCountdown(0);
    setIsCounting(false);
  };

  // Resend OTP
  const handleResendOTP = async () => {
    await handleLogin();
  };

  //Submit when enter key is pressed
  const handleLoginKeyUp = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  //Submit when enter key is pressed
  const handleVerifyOTPKeyUp = (e) => {
    if (e.key === "Enter") {
      handleVerifyOTP();
    }
  };

  const formContent = (
    <>
        {/* Login Form */}
        {!isOTPSent ? (
          <>
            <Box sx={{ marginBottom: 2 }}>
              <label htmlFor="login-email" className="winning-vouchers-form-label">
                <span style={{ color: "red" }}>*</span> Email Address
              </label>
              <input
                type="email"
                id="login-email"
                name="email"
                className={`winning-vouchers-form-input ${loginErrors?.email ? "error" : ""}`}
                value={loginFormData?.email}
                onChange={handleLoginChange}
                placeholder="Enter your email"
              />
              {loginErrors?.email && (
                <p className="winning-vouchers-form-error">
                  {loginErrors.email}
                </p>
              )}
            </Box>

            <Box sx={{ marginBottom: 2 }}>
              <label htmlFor="login-password" className="winning-vouchers-form-label">
                <span style={{ color: "red" }}>*</span> Password
              </label>
              <div className="winning-vouchers-input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  id="login-password"
                  name="password"
                  className={`winning-vouchers-form-input ${loginErrors?.password ? "error" : ""}`}
                  value={loginFormData?.password}
                  onChange={handleLoginChange}
                  onKeyUp={handleLoginKeyUp}
                  placeholder="Enter your password"
                />
                <span
                  className={`winning-vouchers-input-toggle ${loginErrors?.password ? "error" : ""}`}
                  onClick={() => setShowPassword(!showPassword)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </span>
              </div>
              {loginErrors?.password && (
                <p className="winning-vouchers-form-error">
                  {loginErrors.password}
                </p>
              )}
            </Box>

            {loginErrors?.formError && (
              <Box className="winning-vouchers-form-error-box" sx={{ marginBottom: 2 }}>
                <p>{loginErrors.formError}</p>
              </Box>
            )}

            <button
              type="button"
              onClick={handleLogin}
              disabled={isLoginLoading}
              className="winning-vouchers-btn winning-vouchers-btn-primary"
              style={{ width: "100%", marginTop: "8px" }}
            >
              {isLoginLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                  <span>Processing...</span>
                </Box>
              ) : (
                "Send OTP"
              )}
            </button>
          </>
        ) : (
          <>
            {loginSuccess && (
              <Box className="winning-vouchers-form-success" sx={{ marginBottom: 2 }}>
                <p>
                  {loginSuccess}{" "}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEmailChange();
                    }}
                    style={{ color: "var(--agent-primary-dark)", fontWeight: 600 }}
                  >
                    Change
                  </a>
                </p>
              </Box>
            )}

            <Box sx={{ marginBottom: 2 }}>
              <label htmlFor="login-otp" className="winning-vouchers-form-label">
                <span style={{ color: "red" }}>*</span> One Time Password (OTP)
              </label>
              <input
                type="text"
                id="login-otp"
                name="otp"
                className={`winning-vouchers-form-input ${loginErrors?.otp ? "error" : ""}`}
                value={loginFormData?.otp}
                onChange={handleLoginChange}
                onKeyUp={handleVerifyOTPKeyUp}
                style={{
                  textAlign: "center",
                  letterSpacing: "4px",
                }}
                placeholder="Enter OTP"
                maxLength={6}
              />
              {loginErrors?.otp && (
                <p className="winning-vouchers-form-error">
                  {loginErrors.otp}
                </p>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 2,
              }}
            >
              <span style={{ color: "#6b7280", fontSize: "14px" }}>
                {isCounting ? `Resend OTP in ${countdown}s` : ""}
              </span>
              {!isCounting && (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleResendOTP();
                  }}
                  style={{ color: "var(--agent-accent)", fontSize: "14px", fontWeight: 600 }}
                >
                  Resend OTP
                </a>
              )}
            </Box>

            {loginErrors?.formError && (
              <Box className="winning-vouchers-form-error-box" sx={{ marginBottom: 2 }}>
                <p>{loginErrors.formError}</p>
              </Box>
            )}

            <button
              type="button"
              onClick={handleVerifyOTP}
              disabled={isLoginLoading}
              className="winning-vouchers-btn winning-vouchers-btn-primary"
              style={{ width: "100%", marginTop: "8px" }}
            >
              {isLoginLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                  <span>Verifying...</span>
                </Box>
              ) : (
                "Verify & Login"
              )}
            </button>
          </>
        )}
    </>
  );

  if (embedded) {
    if (!open) return null;
    return <div className="popup-content">{formContent}</div>;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={typeof window !== "undefined" && window.innerWidth < 480}
      PaperProps={{
        className: "popup-container",
        style: {
          borderRadius: typeof window !== "undefined" && window.innerWidth < 480 ? 0 : 20,
          padding: 0,
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(15, 23, 42, 0.55)",
          backdropFilter: "blur(6px)",
        },
      }}
    >
      <Box sx={{ padding: { xs: "20px", sm: "28px", md: "32px" } }}>
        <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "var(--agent-gradient)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 8px 24px rgba(13, 148, 136, 0.35)",
            }}
          >
            <FontAwesomeIcon icon={faLock} style={{ fontSize: "24px" }} />
          </Box>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            marginBottom: 0.5,
            color: "var(--agent-text)",
          }}
        >
          Authentication Required
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "var(--agent-text-muted)", textAlign: "center", marginBottom: 2.5 }}
        >
          Please login to access your assigned winning vouchers
        </Typography>
        {formContent}
      </Box>
    </Dialog>
  );
};

export default LoginPopup;
